import { useStrapiQuery } from 'hooks/useStrapiQuery';
import { isNil } from 'ramda';
import { type Dispatch, type SetStateAction, useEffect } from 'react';
import type { RuleGroupType } from 'react-querybuilder';
import type { ConditionFieldName } from 'routes/reseller-data/const';
import type { ID } from '@enmon/commons/lib/tools';
import { useMeters } from 'api/hooks';
import { GET_INVENTORY_METER, type InventoryMeterData } from '../queries/getInventoryMeter';

const PREDEFINED_FIELDS: ReadonlyArray<ConditionFieldName> = [
  'commodity',
  'ownerType',
  'owner',
  'manufacturer',
  'meterModel',
  'manufactured',
  'calibrated',
  'meter_mount_type',
  'diameterNominal',
  'flange',
  'flowrate',
  'woltman',
  'layLength',
  'hotWater',
  'tubeMaterial',
  'phases',
  'mounting',
  'width',
  'iMin',
  'iRef',
  'iMax',
  'circuitBreakerSize',
  'tariffs',
  'isDirect',
  'inputSpec',
  'measuringCurrentTransformer',
  'device_category',
  'gSize',
  'orientation',
  'pMax',
  'qMax',
  'qMin',
  'installationPosition',
  'resistance',
] as const;

const getRulesForInventoryMeter = (value: Partial<Record<(typeof PREDEFINED_FIELDS)[number], unknown>>) =>
  PREDEFINED_FIELDS.filter(field => !isNil(value[field])).map(field => ({
    field,
    operator: '=',
    value: value[field],
  }));

export const usePredefinedQueryFromMeter = (
  inventoryMeterId: ID | undefined,
  setQueryDto: Dispatch<SetStateAction<RuleGroupType>>,
): void => {
  const { data: inventoryMeterData } = useStrapiQuery<InventoryMeterData, { id?: number }>(GET_INVENTORY_METER, {
    variables: {
      id: inventoryMeterId,
    },
    skip: !inventoryMeterId,
  });

  const { data: meterData } = useMeters(
    {
      filter: {
        toolsIds: inventoryMeterId ? [inventoryMeterId] : [],
      },
    },
    {
      enabled: !!inventoryMeterId,
    },
  );

  const meter = meterData?.result?.[0];

  useEffect(() => {
    setQueryDto(prevQuery => ({
      ...prevQuery,
      combinator: 'and',
      rules: getRulesForInventoryMeter({
        ...(meter ?? {}),
        meterModel: meter?.meterModel,
        manufacturer: meter?.manufacturer,
        ...(meter?.detail ?? {}),
        ...(inventoryMeterData?.inventoryMeter.data?.meter_mount_type && {
          meter_mount_type: inventoryMeterData?.inventoryMeter.data?.meter_mount_type,
        }),
      }),
    }));
  }, [inventoryMeterData, meter, setQueryDto]);
};
